import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { clearAllMultiOrgFlags } from '@/lib/localStorage/multiOrgFlag';
import { clearAllPersistedAvailableOrgs } from '@/lib/localStorage/persistedAvailableOrgs';
import { clearAllPersistedDashboardFold } from '@/lib/localStorage/persistedDashboardFold';
import { clearAllPersistedDashboardPrefetch } from '@/lib/localStorage/persistedDashboardPrefetch';
import { clearAllPersistedFeatureFlags } from '@/lib/localStorage/persistedFeatureFlags';
import { clearAllPersistedObjectTypes } from '@/lib/localStorage/persistedObjectTypes';
import { clearAllPersistedOnboardingComplete } from '@/lib/localStorage/persistedOnboardingComplete';
import { clearAllPersistedPipelines } from '@/lib/localStorage/persistedPipelines';
import { clearAllPersistedUserProfile } from '@/lib/localStorage/persistedUserProfile';
import { queryClient } from '@/queryClient';

import { logout } from '../../lib/allauth.js';

export default function LogoutPage() {
  const navigate = useNavigate();
  useEffect(() => {
    logout()
      .then(async () => {
        try {
          // Clear user/session-specific localStorage keys on logout
          // Preserve device preferences (theme, UI sizing)
          clearAllMultiOrgFlags();
          clearAllPersistedFeatureFlags();
          clearAllPersistedDashboardPrefetch();
          clearAllPersistedDashboardFold();
          clearAllPersistedPipelines();
          clearAllPersistedObjectTypes();
          clearAllPersistedUserProfile();
          clearAllPersistedAvailableOrgs();
          clearAllPersistedOnboardingComplete();
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (
              key &&
              (key.startsWith('onboarding_import_dismissed_') ||
                key.startsWith('onboarding_import_has_seen_active_') ||
                key === 'currentOrgId')
            ) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
        } catch {
          // localStorage may be unavailable in private browsing or when storage is blocked
          // Continue with navigation regardless
        } finally {
          queryClient.removeQueries({ queryKey: ['featureFlags'] });
          queryClient.removeQueries({ queryKey: ['pipelines'] });
          queryClient.removeQueries({ queryKey: ['objectTypes'] });
          queryClient.removeQueries({ queryKey: ['userProfile'] });
          queryClient.removeQueries({ queryKey: ['availableOrgs'] });
          queryClient.removeQueries({ queryKey: ['onboarding-status'] });
          // Join-onboarding status is per-user and cached with staleTime:Infinity.
          // Logout navigates within the SPA (no full reload), so without this the
          // next user in the same tab could read the prior user's status and be
          // wrongly redirected into (or past) the invited-user wizard.
          queryClient.removeQueries({ queryKey: ['join-onboarding'] });
        }
        navigate('/');
      })
      .catch((e: unknown) => {
        console.error(e);
      });
  }, [navigate]);

  return null;
}
